// Google Drive Integration & Image Storage Manager
// Handles Google OAuth2 access token, folder configuration, and direct Drive multipart upload.
import { auth, OAUTH_CLIENT_ID } from "./firebase";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { extractDriveId } from "./driveImage";

export const DRIVE_SCOPES = ["https://www.googleapis.com/auth/drive"];

/**
 * ============================================================================
 * GOOGLE DRIVE FOLDER CONFIGURATION (WHERE TO PUT FOLDER LINK IN CODE)
 * ============================================================================
 * You can set or change your default Google Drive folder link or folder ID right here.
 * When you have a folder link like:
 * https://drive.google.com/drive/folders/1hVSBe35kV559UiHkaI1fsMTnPbDyc5Pm?usp=sharing
 * 
 * 1. The ID is the alphanumeric string after /folders/ -> "1hVSBe35kV559UiHkaI1fsMTnPbDyc5Pm"
 * 2. Put it in DEFAULT_DRIVE_FOLDER_ID below:
 * ============================================================================
 */
export const DEFAULT_DRIVE_FOLDER_ID = "1hVSBe35kV559UiHkaI1fsMTnPbDyc5Pm";
export const DEFAULT_DRIVE_FOLDER_URL = `https://drive.google.com/drive/folders/${DEFAULT_DRIVE_FOLDER_ID}?usp=sharing`;

const FOLDER_STORAGE_KEY = "lc_drive_product_folder";

// In-memory token cache (never stored in localStorage as required by security policy)
let cachedAccessToken = null;
let cachedUserEmail = null;
let isSigningIn = false;

// Get configured folder link or ID
export function getDriveFolderConfig() {
  const custom = typeof window !== "undefined" ? localStorage.getItem(FOLDER_STORAGE_KEY) : null;
  const folder = custom || process.env.REACT_APP_GOOGLE_DRIVE_FOLDER || DEFAULT_DRIVE_FOLDER_URL;
  const folderId = extractDriveFolderId(folder) || DEFAULT_DRIVE_FOLDER_ID;
  return {
    folderUrl: folder,
    folderId,
    isDefault: !custom && !process.env.REACT_APP_GOOGLE_DRIVE_FOLDER,
  };
}

export function setDriveFolderConfig(folderUrlOrId) {
  if (!folderUrlOrId || !folderUrlOrId.trim()) {
    localStorage.removeItem(FOLDER_STORAGE_KEY);
    return;
  }
  localStorage.setItem(FOLDER_STORAGE_KEY, folderUrlOrId.trim());
}

export function extractDriveFolderId(urlOrId) {
  if (!urlOrId || typeof urlOrId !== "string") return null;
  const trimmed = urlOrId.trim();
  // If it's already an alphanumeric ID without slashes
  if (/^[a-zA-Z0-9_-]{15,}$/.test(trimmed)) return trimmed;
  // Match /folders/<ID>
  const match = trimmed.match(/\/folders\/([a-zA-Z0-9_-]{15,})/);
  if (match) return match[1];
  // Match ?id=<ID>
  const idMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]{15,})/);
  if (idMatch) return idMatch[1];
  return extractDriveId(trimmed);
}

// In-memory token and auth state getters
export function getDriveAccessToken() {
  return cachedAccessToken;
}

export function hasDriveAccessToken() {
  return Boolean(cachedAccessToken);
}

export function getDriveUserEmail() {
  return cachedUserEmail || (auth?.currentUser?.email ?? null);
}

export function setDriveAccessToken(token, email = null) {
  cachedAccessToken = token;
  if (email) cachedUserEmail = email;
}

export function disconnectGoogleDrive() {
  cachedAccessToken = null;
  cachedUserEmail = null;
}

// Direct Google Identity Services (GIS) OAuth2 client
// Bypasses Firebase Auth backend restrictions (such as auth/operation-not-allowed)
export async function requestTokenViaGIS() {
  if (typeof window === "undefined") {
    throw new Error("Google Identity Services is only available in the browser.");
  }

  // Ensure GIS library is available
  if (!window.google?.accounts?.oauth2) {
    await new Promise((resolve, reject) => {
      const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existing) {
        existing.addEventListener("load", resolve);
        existing.addEventListener("error", reject);
        if (window.google?.accounts?.oauth2) resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load Google Identity Services library."));
      document.head.appendChild(script);
    });
  }

  const clientId = OAUTH_CLIENT_ID;
  if (!clientId) {
    throw new Error("Missing Google OAuth Client ID.");
  }

  return new Promise((resolve, reject) => {
    try {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: "https://www.googleapis.com/auth/drive",
        callback: async (response) => {
          if (response.error) {
            console.error("GIS token error:", response);
            reject(new Error(response.error_description || response.error));
            return;
          }
          if (response.access_token) {
            cachedAccessToken = response.access_token;
            try {
              const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
                headers: { Authorization: `Bearer ${response.access_token}` },
              });
              if (res.ok) {
                const info = await res.json();
                if (info.email) cachedUserEmail = info.email;
              }
            } catch {
              // Ignore email fetch failure; access token is valid
            }
            resolve(response.access_token);
          } else {
            reject(new Error("No access token returned from Google."));
          }
        },
        error_callback: (nonOAuthErr) => {
          console.error("GIS error callback:", nonOAuthErr);
          reject(nonOAuthErr);
        },
      });

      tokenClient.requestAccessToken({ prompt: "" });
    } catch (e) {
      reject(e);
    }
  });
}

// Request or refresh Google OAuth token via Firebase or Google Identity Services
// Must be called from an explicit button click / user gesture to avoid popup blockers
export async function authenticateGoogleDrive() {
  if (cachedAccessToken) return cachedAccessToken;
  if (isSigningIn) {
    throw new Error("Google authentication is already in progress. Please complete the popup.");
  }

  isSigningIn = true;
  try {
    // Primary method: Firebase Auth signInWithPopup
    // Firebase Auth delegates the OAuth popup to https://<authDomain>/__/auth/handler,
    // which has its origin pre-registered in Firebase and avoids origin_mismatch errors.
    if (auth) {
      try {
        const provider = new GoogleAuthProvider();
        provider.addScope("https://www.googleapis.com/auth/drive");
        provider.setCustomParameters({ prompt: "select_account" });

        const result = await signInWithPopup(auth, provider);
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential?.accessToken || result?._tokenResponse?.oauthAccessToken;
        if (token) {
          cachedAccessToken = token;
          cachedUserEmail = result.user?.email || null;
          return cachedAccessToken;
        }
      } catch (fbErr) {
        console.warn("Firebase Auth signInWithPopup encountered:", fbErr);
        // If popup was blocked or closed by user, rethrow
        if (fbErr.code === "auth/popup-blocked" || fbErr.code === "auth/popup-closed-by-user" || fbErr.code === "auth/cancelled-popup-request") {
          throw fbErr;
        }
        if (fbErr.code === "auth/operation-not-allowed") {
          // Attempt secondary GIS if available
          try {
            return await requestTokenViaGIS();
          } catch (gisErr) {
            console.error("GIS fallback also failed:", gisErr);
            const err = new Error(
              "Google Sign-In is not enabled yet in your Firebase Project (codewat-6a17a). Please visit Firebase Console > Authentication > Sign-in method and enable 'Google'."
            );
            err.code = "auth/operation-not-allowed";
            throw err;
          }
        }
        if (fbErr.code === "auth/unauthorized-domain") {
          const err = new Error(
            `This domain (${window.location.hostname}) is not in Firebase Auth's Authorized Domains. Add it under Firebase Console > Authentication > Settings > Authorized domains.`
          );
          err.code = "auth/unauthorized-domain";
          throw err;
        }
        // If other error, try GIS fallback
        try {
          return await requestTokenViaGIS();
        } catch {
          throw fbErr;
        }
      }
    }

    // Secondary method: Direct Google Identity Services (GIS) client
    const token = await requestTokenViaGIS();
    return token;
  } catch (err) {
    if (err.code === "auth/popup-blocked" || err.message?.includes("popup-blocked")) {
      const customErr = new Error(
        "Pop-up blocked: Your browser blocked the Google Sign-In pop-up. Please allow pop-ups for this site, or open the preview in a new tab."
      );
      customErr.code = "auth/popup-blocked";
      customErr.isPopupBlocked = true;
      throw customErr;
    }
    if (err.message?.includes("origin_mismatch") || err.type === "origin_mismatch" || err.error === "origin_mismatch") {
      const customErr = new Error(
        `Error 400: origin_mismatch. The current domain (${window.location.origin}) is not registered under Authorized JavaScript Origins in Google Cloud Console.`
      );
      customErr.code = "origin_mismatch";
      throw customErr;
    }
    throw err;
  } finally {
    isSigningIn = false;
  }
}

/**
 * Uploads a file directly to Google Drive into the configured folder.
 * Uses Google Drive REST API v3 multipart upload.
 * Returns the lh3 Google CDN direct image URL: `https://lh3.googleusercontent.com/d/${fileId}=w1200`
 */
export async function uploadFileToDrive(file, customFolderId = null) {
  // If token is missing, attempt authentication (succeeds if called in user gesture)
  let token = cachedAccessToken;
  if (!token) {
    token = await authenticateGoogleDrive();
  }

  const folderId = customFolderId || getDriveFolderConfig().folderId;

  const metadata = {
    name: `${Date.now()}_${file.name}`,
    mimeType: file.type || "image/jpeg",
    parents: folderId ? [folderId] : [],
  };

  const boundary = "-------314159265358979323846";
  const delimiter = "\r\n--" + boundary + "\r\n";
  const closeDelim = "\r\n--" + boundary + "--";

  const fileData = await file.arrayBuffer();

  const metadataBlob = new Blob([JSON.stringify(metadata)], { type: "application/json; charset=UTF-8" });

  const multipartBody = new Blob(
    [
      delimiter,
      "Content-Type: application/json; charset=UTF-8\r\n\r\n",
      metadataBlob,
      delimiter,
      `Content-Type: ${file.type || "image/jpeg"}\r\n\r\n`,
      fileData,
      closeDelim,
    ],
    { type: `multipart/related; boundary=${boundary}` }
  );

  let res = await fetch(
    "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webContentLink,webViewLink",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: multipartBody,
    }
  );

  // If failed with 403/404 and a parent folder was specified, attempt fallback upload to root
  if (!res.ok && folderId && (res.status === 403 || res.status === 404)) {
    try {
      const rootMetadata = {
        name: `${Date.now()}_${file.name}`,
        mimeType: file.type || "image/jpeg",
      };
      const rootMetadataBlob = new Blob([JSON.stringify(rootMetadata)], { type: "application/json; charset=UTF-8" });
      const rootMultipartBody = new Blob(
        [
          delimiter,
          "Content-Type: application/json; charset=UTF-8\r\n\r\n",
          rootMetadataBlob,
          delimiter,
          `Content-Type: ${file.type || "image/jpeg"}\r\n\r\n`,
          fileData,
          closeDelim,
        ],
        { type: `multipart/related; boundary=${boundary}` }
      );
      const fallbackRes = await fetch(
        "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webContentLink,webViewLink",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: rootMultipartBody,
        }
      );
      if (fallbackRes.ok) {
        res = fallbackRes;
      }
    } catch {
      // Continue with original response handling
    }
  }

  if (!res.ok) {
    let errMsg = "Drive upload failed";
    try {
      const errJson = await res.json();
      errMsg = errJson.error?.message || errMsg;
      // Invalidate cached token on 401 or 403 so user can reconnect with updated scopes
      if (res.status === 401 || res.status === 403) {
        cachedAccessToken = null;
        cachedUserEmail = null;
      }
    } catch {
      // ignore json parse error
    }
    if (res.status === 403 && errMsg.toLowerCase().includes("insufficient")) {
      throw new Error(
        "Google Drive permissions have been upgraded. Please click 'Reconnect Google Drive' in the Admin panel to refresh your login."
      );
    }
    throw new Error(`Google Drive API error (${res.status}): ${errMsg}`);
  }

  const uploaded = await res.json();
  const fileId = uploaded.id;
  if (!fileId) throw new Error("Upload completed but no file ID returned from Drive");

  // Make file publicly readable so users can see product images on website
  try {
    await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}/permissions`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        role: "reader",
        type: "anyone",
      }),
    });
  } catch (permErr) {
    console.warn("Could not set anyone-reader permission on uploaded Drive file:", permErr);
  }

  // Return the direct CDN embeddable URL
  return `https://lh3.googleusercontent.com/d/${fileId}=w1200`;
}
